import React, { Component } from "react";
import { Redirect } from "react-router-dom";
import {
  Grid,
  Segment,
  Table,
  Button,
  Icon,
  Modal,
  Form,
  Input,
  Dropdown,
  Message,
  Header,
  Loader,
  Label,
} from "semantic-ui-react";

import ClubRoster from "./ClubRoster";
import ClubApproval from "./ClubApproval";
import { formatName } from "../../utils/nameUtils";
import { formatSchoolYear } from "../../utils/quarterUtils";
import { statusLabel } from "../../utils/workflowStatus";
import { STATUS_COLOR, awaitsUser, waitingLabel } from "../../utils/clubWorkflow";


const ClubsPV = (props) => {
  const {
    auth,
    userType,
    clubs,
    teachers,
    learners,
    selectedClubId,
    members,
    memberCounts,
    details,
    loaded,
    busy,
    error,
    notice,
    schoolYear,
    currentQuarter,
    permissions,
  } = props;

  const may = {
    administers: false,
    seesAll: false,
    moderatedClubIds: [],
    mayOpen: false,
    mayEnterMarks: () => false,
    ...(permissions || {}),
  };

  if (!auth) return <Redirect to="/" />;
  if (!loaded) return <Loader active inline="centered" />;
  if (!may.mayOpen) {
    return (
      <Segment className="module-panel">
        <Message>
          You are not the moderator of any club, and clubs are not yours to set
          up or oversee. Ask the registrar if you should be.
        </Message>
      </Segment>
    );
  }

  // A moderator who neither administers nor supervises sees only their own,
  // the way a subject teacher sees only the classes they teach.
  const visible = may.seesAll
    ? clubs
    : clubs.filter((c) => may.moderatedClubIds.indexOf(c.clubId) !== -1);

  const selected = visible.filter((c) => c.clubId === selectedClubId)[0];

  return (
    <Segment className="module-panel">
      <Grid>
        {(error || notice) && (
          <Grid.Row>
            <Grid.Column width={16}>
              <Message
                negative={Boolean(error)}
                positive={!error && Boolean(notice)}
                onDismiss={props.onDismiss}
              >
                {error || notice}
              </Message>
            </Grid.Column>
          </Grid.Row>
        )}

        <Grid.Row>
          <Grid.Column width={16}>
            <Header as="h3">
              Clubs
              {schoolYear ? ` - ${formatSchoolYear(schoolYear)}` : ""}
              <Header.Subheader>
                {may.administers
                  ? "A club can hold learners from more than one grade level. Add the club here, then choose Members on its row to put learners in. Marks are entered by the club's own moderator."
                  : may.seesAll
                    ? "Every club this school year. Choose Members on any of them to see who is in it and how they are marked."
                    : "Choose Members on your club to enter this term's marks. The registrar decides who is in it."}
              </Header.Subheader>
            </Header>
            {may.administers && (
              <ClubForm teachers={teachers} busy={busy} onSave={props.onSaveClub} />
            )}
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={16}>
            {visible.length === 0 ? (
              <Message>
                No clubs yet for this school year. Add one above, then put its
                members in.
              </Message>
            ) : (
              <Table celled selectable size="small">
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Club</Table.HeaderCell>
                    <Table.HeaderCell>Moderator</Table.HeaderCell>
                    <Table.HeaderCell textAlign="center">
                      {`Term ${currentQuarter}`}
                    </Table.HeaderCell>
                    <Table.HeaderCell collapsing textAlign="center">
                      Members
                    </Table.HeaderCell>
                    <Table.HeaderCell collapsing textAlign="center">
                      Actions
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {visible.map((club) => (
                    <Table.Row
                      key={club.clubId}
                      active={club.clubId === selectedClubId}
                      onClick={() => props.onSelectClub(club.clubId)}
                      style={{ cursor: "pointer" }}
                    >
                      <Table.Cell>{club.name}</Table.Cell>
                      <Table.Cell>
                        {club.teacher
                          ? `${formatName(club.teacher.firstName)} ${formatName(
                              club.teacher.lastName
                            )}`
                          : "No moderator assigned"}
                      </Table.Cell>
                      {/* The whole point of a list: which clubs are waiting on
                          you, without opening thirteen of them one at a time. */}
                      <Table.Cell textAlign="center">
                        {(() => {
                          const detail = (details || {})[club.clubId];
                          const status = (detail && detail.status) || "To Do";
                          const mine = awaitsUser(status, {
                            userType: userType,
                            isModerator:
                              may.moderatedClubIds.indexOf(club.clubId) !== -1,
                          });
                          return (
                            <span>
                              <Label
                                color={STATUS_COLOR[status] || "grey"}
                                size="small"
                                horizontal
                              >
                                {statusLabel(status)}
                              </Label>
                              {mine ? (
                                <strong>Your move</strong>
                              ) : (
                                <span>{waitingLabel(status)}</span>
                              )}
                            </span>
                          );
                        })()}
                      </Table.Cell>
                      <Table.Cell collapsing textAlign="center">
                        <Button
                          primary={club.clubId === selectedClubId}
                          size="tiny"
                          icon
                          labelPosition="left"
                          onClick={(e) => {
                            e.stopPropagation();
                            props.onSelectClub(club.clubId);
                          }}
                        >
                          <Icon name="users" />
                          {(memberCounts || {})[club.clubId] === undefined
                            ? "Members"
                            : `Members (${memberCounts[club.clubId]})`}
                        </Button>
                      </Table.Cell>
                      <Table.Cell collapsing textAlign="center">
                        {may.administers && (
                        <ClubForm
                          club={club}
                          teachers={teachers}
                          busy={busy}
                          onSave={props.onSaveClub}
                          trigger={
                            <Button icon size="tiny" title="Edit this club">
                              <Icon name="pencil" />
                            </Button>
                          }
                        />
                        )}
                        {may.administers && (
                          <Button
                            icon
                            size="tiny"
                            title="Remove this club"
                            disabled={busy}
                            onClick={(e) => {
                              e.stopPropagation();
                              props.onRemoveClub(club);
                            }}
                          >
                            <Icon name="trash" />
                          </Button>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            )}
          </Grid.Column>
        </Grid.Row>

        {selected && (
          <Grid.Row>
            <Grid.Column width={16}>
              <ClubRoster
                club={selected}
                members={members}
                learners={learners}
                busy={busy}
                schoolYear={schoolYear}
                currentQuarter={currentQuarter}
                mayEditRoster={may.administers}
                mayEnterMarks={may.mayEnterMarks(selected)}
                approval={
                  <ClubApproval
                    club={selected}
                    detail={(details || {})[selected.clubId]}
                    busy={busy}
                    term={`Term ${currentQuarter}`}
                    userType={userType}
                    isModerator={
                      may.moderatedClubIds.indexOf(selected.clubId) !== -1
                    }
                    onAdvance={props.onAdvance}
                    onClubSheetChange={props.onClubSheetChange}
                    sheetName={props.clubSheetName}
                  />
                }
                onAddMember={props.onAddMember}
                onRemoveMember={props.onRemoveMember}
                onSaveMarks={props.onSaveMarks}
              />
            </Grid.Column>
          </Grid.Row>
        )}
      </Grid>
    </Segment>
  );
};

/**
 * Add or edit a club. Rendered as a button when creating and behind a pencil
 * when editing, so one form serves both and they cannot drift apart.
 */
class ClubForm extends Component {
  state = { open: false, name: "", teacherId: "", description: "" };

  open = (e) => {
    if (e) e.stopPropagation();
    const club = this.props.club;
    this.setState({
      open: true,
      name: club ? club.name : "",
      teacherId: club ? club.teacherId || "" : "",
      description: club ? club.description || "" : "",
    });
  };

  close = () => this.setState({ open: false });

  handleChange = (e, { name, value }) => this.setState({ [name]: value });

  save = () => {
    const name = this.state.name.trim();
    if (!name) return;

    this.props.onSave({
      clubId: this.props.club ? this.props.club.clubId : null,
      name: name,
      teacherId: this.state.teacherId,
      description: this.state.description,
    });
    this.close();
  };

  render() {
    const { teachers, busy, trigger } = this.props;

    const options = (teachers || [])
      .map((t) => ({
        key: t.teacherId,
        value: t.teacherId,
        text: `${formatName(t.lastName)}, ${formatName(t.firstName)}`,
      }))
      .sort((a, b) => a.text.localeCompare(b.text));

    return (
      <React.Fragment>
        {trigger ? (
          React.cloneElement(trigger, { onClick: this.open })
        ) : (
          <Button primary icon labelPosition="left" onClick={this.open}>
            <Icon name="plus" />
            Add Club
          </Button>
        )}

        <Modal open={this.state.open} onClose={this.close} size="small" closeIcon>
          <Modal.Header>
            {this.props.club ? "Edit club" : "Add a club"}
          </Modal.Header>
          <Modal.Content>
            <Form>
              <Form.Field
                control={Input}
                label="Club name"
                name="name"
                placeholder="e.g. Art Club"
                value={this.state.name}
                onChange={this.handleChange}
              />
              <Form.Field>
                <label>Moderator</label>
                <Dropdown
                  search
                  selection
                  clearable
                  name="teacherId"
                  placeholder="Teacher who runs this club"
                  options={options}
                  value={this.state.teacherId}
                  onChange={this.handleChange}
                />
              </Form.Field>
              <Form.Field
                control={Input}
                label="Description"
                name="description"
                placeholder="Optional"
                value={this.state.description}
                onChange={this.handleChange}
              />
            </Form>
          </Modal.Content>
          <Modal.Actions>
            <Button
              color="green"
              disabled={busy || !this.state.name.trim()}
              onClick={this.save}
            >
              <Icon name="checkmark" /> Save
            </Button>
          </Modal.Actions>
        </Modal>
      </React.Fragment>
    );
  }
}

export default ClubsPV;
